import { apiUrl } from '../config/api';

const GUEST_CART_KEY = 'eulua_guest_cart';
const roundMoney = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

const asPositiveInteger = (value, fallback = 1) => {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const priceGuestItem = (item, quantity) => {
  const normalizedQuantity = asPositiveInteger(quantity);
  if (item.price_available === false) {
    return {
      ...item,
      quantity: normalizedQuantity,
      product_price: null,
      line_total: null,
      base_line_total: null,
      original_line_total: null,
      has_discount: false,
    };
  }
  const originalUnitPrice = roundMoney(
    item.base_product_price ?? item.original_product_price ?? item.product_price ?? 0
  );
  const discount = [...(item.discounts || [])]
    .sort((a, b) => Number(b.min_quantity || 0) - Number(a.min_quantity || 0))
    .find((candidate) => {
      const minimum = Number(candidate.min_quantity || 0);
      const maximum = candidate.max_quantity === null
        || candidate.max_quantity === undefined
        || Number(candidate.max_quantity) <= 0
        ? Infinity
        : Number(candidate.max_quantity);
      return normalizedQuantity >= minimum && normalizedQuantity <= maximum;
    });

  let unitPrice = originalUnitPrice;
  if (discount?.discount_type === 'percentage') {
    unitPrice = originalUnitPrice * (1 - Number(discount.value || 0) / 100);
  } else if (discount) {
    unitPrice = originalUnitPrice - Number(discount.value || 0);
  }

  const salesPriceIncrease = 0;
  const baseUnitPrice = roundMoney(Math.max(0, unitPrice));
  unitPrice = roundMoney(baseUnitPrice + salesPriceIncrease);
  const customerOriginalUnitPrice = roundMoney(originalUnitPrice + salesPriceIncrease);
  const originalLineTotal = roundMoney(customerOriginalUnitPrice * normalizedQuantity);
  const lineTotal = roundMoney(unitPrice * normalizedQuantity);
  const discountAmount = roundMoney(originalLineTotal - lineTotal);

  return {
    ...item,
    quantity: normalizedQuantity,
    product_price: unitPrice,
    base_unit_price: baseUnitPrice,
    base_original_unit_price: originalUnitPrice,
    base_product_price: originalUnitPrice,
    original_product_price: customerOriginalUnitPrice,
    line_total: lineTotal,
    base_line_total: roundMoney(baseUnitPrice * normalizedQuantity),
    original_line_total: originalLineTotal,
    discount_amount: discountAmount,
    discount_value: discount ? Number(discount.value || 0) : null,
    discount_type: discount?.discount_type || null,
    has_discount: Boolean(discount && discountAmount > 0),
    sales_price_increase: salesPriceIncrease,
    sales_price_increase_total: roundMoney(salesPriceIncrease * normalizedQuantity),
    service_fee_rate: 0,
  };
};

export const getGuestCart = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(GUEST_CART_KEY) || '[]');
    return Array.isArray(stored)
      ? stored.map((item) => priceGuestItem(item, item.quantity))
      : [];
  } catch {
    localStorage.removeItem(GUEST_CART_KEY);
    return [];
  }
};

const saveGuestCart = (items) => {
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
  return items;
};

export const addGuestCartItem = (product, quantity) => {
  const productId = asPositiveInteger(product?.productid, null);
  if (!productId) throw new Error('Could not identify the product');

  const items = getGuestCart();
  const existingIndex = items.findIndex((item) => Number(item.idproducts) === productId);
  const requestedQuantity = asPositiveInteger(quantity);
  const existingQuantity = existingIndex >= 0 ? asPositiveInteger(items[existingIndex].quantity) : 0;
  const seller = product.sellerProfile || {};
  const baseItem = {
    ...(existingIndex >= 0 ? items[existingIndex] : {}),
    idcart: `guest-${productId}`,
    idproducts: productId,
    product_name: product.product_name || product.product_brand || 'Product',
    product_description: product.product_description || '',
    product_image: product.images?.[0] || '',
    product_brand: product.product_brand || '',
    route_slug: product.route_slug || '',
    product_stock: product.product_stock,
    product_mpq: asPositiveInteger(product.product_mpq),
    product_pickup: product.product_pickup,
    sellerid: product.sellerid,
    seller_name: seller.company_name || seller.full_name || `Seller #${product.sellerid}`,
    seller_route_slug: seller.route_slug || '',
    pickup_address: seller.address_seller || '',
    pickup_city: seller.city_seller || product.city_seller || '',
    pickup_state: seller.state_seller || product.state_seller || '',
    pickup_country: seller.country_seller || product.country_seller || '',
    pickup_phone: seller.contact_phone || '',
    shipping_national: seller.shipping_national,
    shipping_international: seller.shipping_international,
    shipping_custom: seller.shipping_custom,
    base_product_price: Number(product.product_price || 0),
    price_available: product.price_available !== false,
    price_currency: product.price_currency || 'USD',
    service_fee_rate: 0,
    discounts: Array.isArray(product.discounts) ? product.discounts : [],
    shippingEstimates: Array.isArray(product.shippingEstimates) ? product.shippingEstimates : [],
  };
  const pricedItem = priceGuestItem(baseItem, existingQuantity + requestedQuantity);

  if (existingIndex >= 0) items[existingIndex] = pricedItem;
  else items.push(pricedItem);
  saveGuestCart(items);
  return pricedItem;
};

export const updateGuestCartQuantity = (productId, quantity) => {
  const items = getGuestCart();
  const index = items.findIndex((item) => Number(item.idproducts) === Number(productId));
  if (index < 0) return items;
  items[index] = priceGuestItem(items[index], quantity);
  return saveGuestCart(items);
};

export const removeGuestCartItem = (productId) => saveGuestCart(
  getGuestCart().filter((item) => Number(item.idproducts) !== Number(productId))
);

let activeSync = null;

export const syncGuestCart = async (token) => {
  if (!token || getGuestCart().length === 0) return;
  if (activeSync) return activeSync;

  activeSync = (async () => {
    const items = getGuestCart();
    for (const item of items) {
      const response = await fetch(apiUrl('/api/cart'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          idproducts: item.idproducts,
          quantity: item.quantity,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Could not sync your cart');
      removeGuestCartItem(item.idproducts);
    }
  })();

  try {
    await activeSync;
  } finally {
    activeSync = null;
  }
};
